import { useEffect, useState } from 'react';

interface Leaf {
  id: number;
  x: number;
  y: number;
  rotation: number;
  scale: number;
  color: string;
  delay: number;
  duration: number;
}

const LEAF_COLORS = ['#e2acb0', '#c4878b', '#8d9fb3', '#7e9265', '#b8c4a8'];

function generateLeaves(count: number): Leaf[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    rotation: Math.random() * 360,
    scale: 0.5 + Math.random() * 0.8,
    color: LEAF_COLORS[Math.floor(Math.random() * LEAF_COLORS.length)],
    delay: Math.random() * 10,
    duration: 15 + Math.random() * 20,
  }));
}

export default function AutumnalDecorations({ intensity = 'light' }: { intensity?: 'light' | 'medium' }) {
  const [leaves, setLeaves] = useState<Leaf[]>([]);

  useEffect(() => {
    const count = intensity === 'light' ? 8 : 15;
    setLeaves(generateLeaves(count));
  }, [intensity]);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-32 h-32 md:w-48 md:h-48 opacity-20">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path
            fill="#e2acb0"
            d="M0 0 Q50 0 50 50 Q0 50 0 0"
          />
          <path
            fill="#8d9fb3"
            d="M10 0 Q40 10 40 40 Q10 40 10 0"
            opacity="0.7"
          />
        </svg>
      </div>

      <div className="absolute top-0 right-0 w-32 h-32 md:w-48 md:h-48 opacity-20 transform scale-x-[-1]">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path
            fill="#c4878b"
            d="M0 0 Q50 0 50 50 Q0 50 0 0"
          />
          <path
            fill="#7e9265"
            d="M10 0 Q40 10 40 40 Q10 40 10 0"
            opacity="0.7"
          />
        </svg>
      </div>

      <div className="absolute bottom-0 left-0 w-32 h-32 md:w-48 md:h-48 opacity-20 transform scale-y-[-1]">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path
            fill="#8d9fb3"
            d="M0 0 Q50 0 50 50 Q0 50 0 0"
          />
          <path
            fill="#e2acb0"
            d="M10 0 Q40 10 40 40 Q10 40 10 0"
            opacity="0.7"
          />
        </svg>
      </div>

      <div className="absolute bottom-0 right-0 w-32 h-32 md:w-48 md:h-48 opacity-20 transform scale-[-1]">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path
            fill="#7e9265"
            d="M0 0 Q50 0 50 50 Q0 50 0 0"
          />
          <path
            fill="#c4878b"
            d="M10 0 Q40 10 40 40 Q10 40 10 0"
            opacity="0.7"
          />
        </svg>
      </div>

      {/* Floating leaves */}
      {leaves.map((leaf) => (
        <div
          key={leaf.id}
          className="absolute animate-drift"
          style={{
            left: `${leaf.x}%`,
            top: `${leaf.y}%`,
            transform: `rotate(${leaf.rotation}deg) scale(${leaf.scale})`,
            animationDelay: `${leaf.delay}s`,
            animationDuration: `${leaf.duration}s`,
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill={leaf.color}
            className="opacity-40"
          >
            <path d="M12 2C8 6 4 9 4 14c0 5 3.5 8 8 8s8-3 8-8c0-5-4-8-8-12z" />
            <path
              d="M12 6v14"
              stroke="#3a3a3a"
              strokeWidth="0.5"
              fill="none"
              opacity="0.5"
            />
          </svg>
        </div>
      ))}
    </div>
  );
}

